import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Timers para evitar fecho acidental no hover
   * @type {number | null}
   */
  #closeTimeout = null;
  #openTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#clearTimeouts();
  }

  #clearTimeouts() {
    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    if (this.#openTimeout) clearTimeout(this.#openTimeout);
    this.#closeTimeout = null;
    this.#openTimeout = null;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Se houver um agendamento de fecho a decorrer, cancela-o
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    // Cancela qualquer abertura pendente anterior
    if (this.#openTimeout) clearTimeout(this.#openTimeout);

    // Pequena tolerância para evitar disparar a animação instantaneamente ao passar o rato rapidamente
    this.#openTimeout = setTimeout(() => {
      this.dispatchEvent(new MegaMenuHoverEvent());

      const isDefaultSlot = event.target.slot === '';
      this.dataset.overflowExpanded = (!isDefaultSlot).toString();

      const previouslyActiveItem = this.#state.activeItem;
      if (previouslyActiveItem) {
        previouslyActiveItem.ariaExpanded = 'false';
      }

      this.#state.activeItem = item;
      this.ariaExpanded = 'true';
      item.ariaExpanded = 'true';

      let submenu = findSubmenu(item);
      const hasSubmenu = Boolean(submenu);

      if (!hasSubmenu && !isDefaultSlot) {
        submenu = this.overflowMenu;
      }

      if (submenu) {
        submenu.dataset.active = '';
        this.#cleanupMutationObserver();

        this.#submenuMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
                this.#cleanupMutationObserver();
              }
            });
          });
        });
        this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

        setTimeout(() => {
          this.#cleanupMutationObserver();
        }, 500);
      }

      let finalHeight = submenu?.offsetHeight || 0;

      if (!isDefaultSlot) {
        const overflowListHeight = this.#getOverflowListLinksHeight();
        if (hasSubmenu) {
          const overflowHeight = this.overflowMenu?.offsetHeight || 0;
          finalHeight = Math.max(overflowHeight, overflowListHeight);
        } else {
          finalHeight = overflowListHeight;
        }
      }

      if (!submenu) {
        finalHeight = 0;
      }

      this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
      this.#setFullOpenHeaderHeight(finalHeight);
      this.style.setProperty('--submenu-opacity', '1');
    }, 50); // Delay mínimo de abertura
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // Cancela abertura se o rato saiu antes do tempo
    if (this.#openTimeout) {
      clearTimeout(this.#openTimeout);
      this.#openTimeout = null;
    }

    // Dá um tempo de carência (300ms) antes de fechar de facto
    if (this.#closeTimeout) clearTimeout(this.#closeTimeout);
    this.#closeTimeout = setTimeout(() => {
      this.#deactivate();
    }, 300);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}